import { browserBlock, runSnapshot, setPersistedCache, versionBlock } from "@/systems/snapshot";
import type { GameState } from "@/state/GameState";

/**
 * Telemetry beacons (v0.12 RFC §3.3, RULED IN): session_start / error /
 * progress-on-hide → ntfy. Per the RULING, EVERY beacon carries the
 * browser block + the compact runSnapshot — appended centrally in
 * beacon(), so no event can forget them (v0.12 review, codex).
 *
 * The DOCUMENTED fail-quiet exception: telemetry is OFF unless this is a
 * PRODUCTION build AND the endpoint env exists (env alone is not enough —
 * a dev build inheriting a .env must never send; v0.12 review). Every
 * send is fire-and-forget; failures are swallowed. Beacons are BOUNDED:
 * errors ≤3/session, progress ≥60s apart and ≤20/session (RFC G4), and
 * every body is clamped to ntfy's 4KB cap — an oversized beacon degrades
 * deterministically instead of vanishing at the server.
 */

const ID_KEY = "halfway.telemetry.id";
const MAX_ERRORS_PER_SESSION = 3;
const MAX_PROGRESS_PER_SESSION = 20;
const MIN_PROGRESS_GAP_MS = 60_000;
const BODY_CAP = 4096;

interface TelemetryEnv {
  url: string;
  token: string;
}

function env(): TelemetryEnv | null {
  if (!import.meta.env.PROD) return null; // dev/e2e NEVER send, env or not
  const url = import.meta.env.VITE_TELEMETRY_URL as string | undefined;
  const token = import.meta.env.VITE_TELEMETRY_TOKEN as string | undefined;
  if (!url || !token) return null;
  return { url, token };
}

/** Anonymous per-browser id, deliberately OUTSIDE the save envelope —
 * New Game keeps the identity (one restarting playtester), a cleared
 * browser starts fresh (RFC §3.4). */
function telemetryId(): string {
  try {
    const existing = localStorage.getItem(ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(ID_KEY, id);
    return id;
  } catch {
    return "no-storage";
  }
}

let errorsSent = 0;
let progressSent = 0;
let lastProgressAt = 0;

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

function beacon(cfg: TelemetryEnv, id: string, getState: () => GameState, event: string, payload: Record<string, unknown>): void {
  // RULED capture on EVERY beacon: browser context + compact game state.
  const full = { event, id, ...versionBlock(), browser: browserBlock(), run: runSnapshot(getState()), ...payload };
  let body = JSON.stringify(full);
  if (byteLength(body) > BODY_CAP) {
    // Deterministic degrade (v0.12 review, codex): drop the payload extras
    // first, then the snapshot — never let ntfy silently eat the beacon.
    body = JSON.stringify({ event, id, ...versionBlock(), browser: browserBlock(), run: runSnapshot(getState()), clamped: true });
    if (byteLength(body) > BODY_CAP) {
      body = JSON.stringify({ event, id, ...versionBlock(), clamped: true });
    }
  }
  void fetch(cfg.url, {
    method: "POST",
    keepalive: true, // survives tab close — the progress beacon's whole point
    headers: { Authorization: `Bearer ${cfg.token}`, "X-Title": `halfway ${event}` },
    body,
  }).catch(() => {
    /* fire-and-forget by contract */
  });
}

/** Strip the one user-authored string that can leak into fail-loud error
 * messages (v0.12 review, security lens): the facility name is quoted by
 * validation throws; it must not ride an "anonymous" error report. */
function scrubUserText(text: string, state: GameState): string {
  const name = state.account.facilityName;
  // typeof-guard, not null-check: a legacy save shape can surface
  // undefined here and undefined !== null (bot leg, gemini).
  if (typeof name === "string" && name.length > 0) {
    return text.split(name).join("[facility]");
  }
  return text;
}

/**
 * Wire the three beacons. Call once at boot with a live-state getter
 * (never a captured snapshot — the hide beacon wants CURRENT state).
 * No-op outside a production build with endpoint env.
 */
export function startTelemetry(getState: () => GameState, opts: { fresh: boolean; offlineTicks: number }): void {
  const cfg = env();
  if (cfg === null) return; // silent-off: the documented exception
  const id = telemetryId();

  // Resolve persisted-storage ONCE into the shared cache, then send the
  // start beacon (a short race just reports "unknown" — acceptable).
  void navigator.storage?.persisted?.()?.then((p) => setPersistedCache(p)); // ?. before .then — same undefined-chain class
  beacon(cfg, id, getState, "session_start", { fresh: opts.fresh, offlineTicks: opts.offlineTicks });

  const onError = (message: string, stack: string | undefined): void => {
    if (errorsSent >= MAX_ERRORS_PER_SESSION) return;
    errorsSent += 1;
    beacon(cfg, id, getState, "error", {
      message: scrubUserText(message.slice(0, 500), getState()),
      top: scrubUserText((stack ?? "").split("\n").slice(0, 2).join(" | ").slice(0, 300), getState()),
    });
  };
  window.addEventListener("error", (e) => onError(String(e.message), e.error instanceof Error ? e.error.stack : undefined));
  window.addEventListener("unhandledrejection", (e) => {
    const r: unknown = e.reason;
    onError(r instanceof Error ? r.message : String(r), r instanceof Error ? r.stack : undefined);
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    // Bounded (RFC G4): app-switch churn must never become a beacon storm.
    const now = Date.now();
    if (progressSent >= MAX_PROGRESS_PER_SESSION || now - lastProgressAt < MIN_PROGRESS_GAP_MS) return;
    progressSent += 1;
    lastProgressAt = now;
    beacon(cfg, id, getState, "progress", {});
  });
}

/** Test-only. */
export function _telemetryEnabled(): boolean {
  return env() !== null;
}
